import { MaintenanceBlockSignal } from './types';

type Listener = (signal: MaintenanceBlockSignal) => void;

let listeners: Listener[] = [];

const FALLBACK_TITLE = 'Temporarily unavailable';
const FALLBACK_MESSAGE =
  'This part of the site is temporarily unavailable while we carry out maintenance. Please try again shortly.';

/** Subscribes to 503 MAINTENANCE answers. Returns the unsubscribe function. */
export function subscribeToMaintenanceBlock(listener: Listener): () => void {
  listeners = [...listeners, listener];
  return () => {
    listeners = listeners.filter((l) => l !== listener);
  };
}

export function signalMaintenanceBlock(signal: MaintenanceBlockSignal): void {
  listeners.forEach((listener) => listener(signal));
}

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

const text = (value: unknown, fallback: string): string =>
  typeof value === 'string' && value.trim().length > 0 ? value : fallback;

/**
 * Turns a 503 answer body into a block signal, or null if the answer is not a
 * maintenance answer (a plain 503 from somewhere else must not look like one).
 */
export function parseMaintenanceBlock(
  status: number,
  body: unknown,
): MaintenanceBlockSignal | null {
  if (status !== 503 || !isRecord(body)) return null;
  const data = isRecord(body.data) ? body.data : {};
  const isMaintenance =
    body.code === 'MAINTENANCE' || body.error === 'MAINTENANCE' || data.code === 'MAINTENANCE';
  if (!isMaintenance) return null;

  const endsAt =
    typeof data.endsAt === 'string' && !Number.isNaN(Date.parse(data.endsAt)) ? data.endsAt : null;
  return {
    area: typeof data.area === 'string' && data.area.length > 0 ? data.area : null,
    title: text(data.title, FALLBACK_TITLE),
    message: text(body.message, FALLBACK_MESSAGE),
    endsAt,
  };
}

/**
 * Called by the central API response handling for every non-OK answer. Does
 * nothing unless the answer is a 503 MAINTENANCE. The status endpoint itself is
 * skipped so a failing status call can never trigger its own refetch loop.
 */
export function reportMaintenanceIfBlocked(status: number, url: string, body: unknown): void {
  if (/\/maintenance(\?|$)/.test(url)) return;
  const signal = parseMaintenanceBlock(status, body);
  if (signal) signalMaintenanceBlock(signal);
}
